/**
 * RuleSelectFieldView — a labelled, closed-vocabulary dropdown used throughout
 * the automation-rule builder (SE-5506).
 *
 * A thin presentational wrapper over the `Select` atom so the builder and the
 * condition-row molecule don't each repeat the SelectTrigger/Portal/Content
 * boilerplate. Options are `{ value, label }` pairs — the builder always feeds
 * these from the live enums / board data, never a hardcoded subset.
 * @remarks View component — no business logic, hooks, or state.
 */

import { ChevronDownIcon } from "lucide-react-native";
import { memo } from "react";

import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** One `{ value, label }` option. */
export interface RuleSelectOption {
  readonly value: string;
  readonly label: string;
}

/** Props for {@link RuleSelectFieldView}. */
export interface RuleSelectFieldViewProps {
  /** Field label shown above the control. */
  readonly label: string;
  /** Placeholder shown when nothing is selected. */
  readonly placeholder: string;
  /** Currently selected value (empty string = none). */
  readonly value: string;
  /** Options to render. */
  readonly options: readonly RuleSelectOption[];
  /** Selection handler (stable reference — never an inline arrow). */
  readonly onValueChange: (value: string) => void;
  /** Whether the control is disabled. */
  readonly isDisabled?: boolean;
  /** testID for the trigger input. */
  readonly testID?: string;
}

/**
 * Resolve the human-readable text for the selected value.
 *
 * The Select family seeds the text it displays from `initialLabel ?? selectedLabel`
 * ONCE, at mount, and only refreshes it when the user presses a SelectItem. Opening
 * the builder to EDIT an existing rule therefore showed the raw `value` — a stage
 * UUID, or a comparator enum such as `EQ` — because no press had ever happened
 * (SE-6855). The same hole reopens on any programmatic change: switching a
 * condition's type reseeds its comparator without a press.
 *
 * Deriving the text from the `options` this component already receives and passing it
 * as the input's own `value` makes the display fully controlled — the family spreads
 * incoming props AFTER its `value={label || value}` default, so ours wins on every
 * render, mount or update. `selectedLabel` is passed alongside so the family's
 * internal state agrees from the first render too.
 * @param options - The options to resolve against.
 * @param value - The selected value.
 * @returns The matching option's label, or "" when nothing matches — options still
 * loading or a stale id then show the placeholder rather than a raw code.
 */
const labelForValue = (
  options: readonly RuleSelectOption[],
  value: string
): string => options.find(option => option.value === value)?.label ?? "";

/**
 * Render a labelled Select.
 * @param props - {@link RuleSelectFieldViewProps}
 * @param props.label - Field label
 * @param props.placeholder - Placeholder text
 * @param props.value - Selected value
 * @param props.options - Options to render
 * @param props.onValueChange - Selection handler
 * @param props.isDisabled - Whether disabled
 * @param props.testID - testID for the trigger input
 * @returns The labelled select.
 */
const RuleSelectFieldView = ({
  label,
  placeholder,
  value,
  options,
  onValueChange,
  isDisabled = false,
  testID,
}: RuleSelectFieldViewProps) => (
  <Stack className="gap-1">
    <Text variant="body-strong" className="text-content-secondary">
      {label}
    </Text>
    <Select
      selectedValue={value}
      selectedLabel={labelForValue(options, value)}
      onValueChange={onValueChange}
      isDisabled={isDisabled}
    >
      <SelectTrigger variant="outline" size="md" className="pr-3">
        <SelectInput
          testID={testID}
          placeholder={placeholder}
          value={labelForValue(options, value)}
          className="flex-1"
        />
        <SelectIcon as={ChevronDownIcon} />
      </SelectTrigger>
      <SelectPortal>
        <SelectBackdrop />
        <SelectContent>
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>
          {options.map(option => (
            <SelectItem
              key={option.value}
              label={option.label}
              value={option.value}
            />
          ))}
        </SelectContent>
      </SelectPortal>
    </Select>
  </Stack>
);

RuleSelectFieldView.displayName = "RuleSelectFieldView";

export default memo(RuleSelectFieldView);
